import React, { useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { useParams } from 'react-router'
import { appendJDLinkToHeapLocation, trackAnalyticEvent, ANALYTICS_LOCATIONS, isInIframe } from '../../../utils'
import { OAuthIntegrationCard } from '../oauth-integration-card/oauth-integration-card'
import { OAuthIntegrationAdditionalCard } from '../oauth-integration-additional-card/oauth-integration-additional-card'
import { OAuthIntegrationConnectedCard } from '../oauth-integration-connected-card/oauth-integration-connected-card'
import { useFetchFeedOAuthUrlMutation, useGetFeedQuery } from '../../../graphql'
import { ActiveFeedStatus } from '../../../types'
import { useModal } from '../../../providers'
import { LoadingContainer } from './oauth-integration-manage.styles'

type Props ={
	isEditing: boolean,
}

export const OAuthIntegrationManage: React.FC<Props> = ( { isEditing } ) => {
	const params = useParams<{ id: string }>()
	const feedId = params.id ?? ''
	const { data: feedData, isLoading } = useGetFeedQuery( { feedId } )
	const { mutateAsync: fetchFeedOAuthUrl } = useFetchFeedOAuthUrlMutation()
	const { showModal } = useModal()
	const [ isConnecting, setIsConnecting ] = useState( false )
	const isInAnIframe = useRef( isInIframe() ).current

	if ( isLoading ) {
		// show a mui spinner icon in the left
		return (
			<LoadingContainer>
				<CircularProgress />
			</LoadingContainer>
		)
	}

	if ( !feedData?.feed?.feed ) {
		return <div>Feed not found</div>
	}


	const onConnectPress = async() => {
		try {
			setIsConnecting( true )
			const oAuthUrlResponse = await fetchFeedOAuthUrl( { feedId } )
			if ( !oAuthUrlResponse.feedOAuthURL ) {
				throw new Error( 'No OAuth URL returned from server.' )
			}
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if ( isInAnIframe ) {
				window.parent.postMessage( {
					type: 'navigate',
					target: '_self',
					href: oAuthUrlResponse.feedOAuthURL,
				}, '*' )
				return
			} else {
				window.location.href = oAuthUrlResponse.feedOAuthURL
			}
		} catch ( error ) {
			showModal( {
				title: 'Error Fetching OAuth URL',
				headerText: 'There was a problem connecting to the integration',
				content: `Please try again later. Contact support if the issue persists. ${( error as Error ).message}`,
			} )
		} finally {
			setIsConnecting( false )
		}
		trackAnalyticEvent(
			ANALYTICS_LOCATIONS.MANAGE_PAGE,
			'Click',
			appendJDLinkToHeapLocation( 'Reconnect', integrationName ),
		)
	}


	const feedStatus = feedData.feed.feed.currentStatus
	const isPartiallyConnected = feedData.feed.feed.currentStatus === ActiveFeedStatus.ACTION_NEEDED && !isEditing
	const isAdditionalActionDisabled = feedStatus !== ActiveFeedStatus.ACTION_NEEDED && ( !isEditing || feedStatus === ActiveFeedStatus.AUTH_FAILED )
	const isFullyConnected = feedStatus !== ActiveFeedStatus.AUTH_FAILED && !isEditing && !isPartiallyConnected

	const { callToActionText, callToActionUrl } = feedData.feed.feed
	const integrationName = feedData.feed.feed.integration?.name ?? ''
	const supportsPostAuthAction = feedData.feed.feed.integration?.supportsPostAuth ?? false

	const renderAdditionalSection = () => {
		if ( !supportsPostAuthAction ) {
			return null
		}

		if ( isFullyConnected ) {
			return <OAuthIntegrationConnectedCard title="Access to T3 Enabled" />
		}

		if ( callToActionUrl && callToActionText ) {
			return (
				<OAuthIntegrationAdditionalCard
					isAdditionalActionDisabled={isAdditionalActionDisabled}
					additionalUrl={callToActionUrl}
					additionalUrlText={callToActionText}
					integrationTitle={integrationName}
					buttonText="Update T3 Access"
				/>
			)
		}
		return null
	}

	return (
		<>
			<OAuthIntegrationCard
				isConnecting={isConnecting}
				isEditing={isEditing}
				isFullyConnected={isFullyConnected}
				isPartiallyConnected={isPartiallyConnected}
				integrationName={integrationName}
				helpUrls={feedData.feed.feed.integration?.helpUrls}
				onPress={onConnectPress}
				buttonText={`Reconnect to ${integrationName}`}
			/>
			{renderAdditionalSection()}
		</>
	)
}
